import React from "react";
import { Link } from "react-router-dom";
import { useI18n, langPath } from "@/i18n/I18nContext";
import ContactForm from "@/components/ContactForm";
import {
  ArrowRight,
  ArrowUpRight,
  Building,
  LineChart,
  ShieldCheck,
  Users,
  ClipboardCheck,
  Coins,
  Eye,
  Wallet,
  MapPin,
  Phone,
  Mail,
  Check,
} from "lucide-react";

// Croatian high-rise / residential new build imagery
const PROJECT_IMAGES = [
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1400&q=70", // modern residential tower
  "https://images.unsplash.com/photo-1470723710355-95304d8aece4?auto=format&fit=crop&w=1400&q=70", // coastal apartment building
  "https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=1400&q=70", // urban residential facade
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1600&q=70"; // modern residential high-rises
const INTRO_IMG =
  "https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=1400&q=70"; // construction / new build
const MISSION_IMG =
  "https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=1400&q=70"; // Zagreb-style residential district

function Stat({ value, label }) {
  return (
    <div className="flex flex-col">
      <div className="font-display text-[36px] md:text-[44px] leading-none" style={{ color: "var(--z-ink)" }}>
        {value}
      </div>
      <div className="mt-2 text-[13px] text-[color:var(--z-muted)] uppercase tracking-wider">{label}</div>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();

  return (
    <main>
      {/* HERO */}
      <section className="z-hero">
        <div className="z-container pt-14 md:pt-20 pb-16">
          <div className="grid md:grid-cols-[1.15fr_1fr] gap-10 md:gap-16 items-center">
            <div className="z-fade-up">
              <div className="z-chip mb-5"><span className="dot"/>{t("hero.eyebrow")}</div>
              <h1 className="font-display text-[42px] sm:text-[54px] md:text-[64px] leading-[1.02] tracking-tight">
                {t("hero.title")}
              </h1>
              <p className="mt-5 text-[16px] md:text-[17px] max-w-[560px] text-[color:var(--z-ink-2)]">
                {t("hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={langPath(lang) + "#contact"} className="z-btn z-btn-primary" data-testid="hero-cta-primary">
                  {t("hero.ctaPrimary")} <ArrowRight size={16}/>
                </a>
                <a href={langPath(lang) + "#how"} className="z-btn z-btn-outline" data-testid="hero-cta-secondary">
                  {t("hero.ctaSecondary")}
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/5] rounded-[28px] overflow-hidden border border-[color:var(--z-line)]">
                <img src={HERO_IMG} alt="Croatian residential tower" className="z-img" loading="eager"/>
              </div>
              <div className="absolute -left-6 -bottom-6 hidden sm:block z-card w-[240px]">
                <div className="flex items-center gap-2 text-[12px] text-[color:var(--z-muted)] uppercase tracking-wider">
                  <ShieldCheck size={14}/> HANFA
                </div>
                <p className="mt-2 text-[13px] leading-snug">
                  {lang === "hr"
                    ? "Usklađeno s hrvatskim propisima i EU regulativom 2020/1503."
                    : "Compliant with Croatian rules and EU Regulation 2020/1503."}
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[color:var(--z-line)] pt-10">
            <Stat value={t("hero.stat1Value")} label={t("hero.stat1Label")}/>
            <Stat value={t("hero.stat2Value")} label={t("hero.stat2Label")}/>
            <Stat value={t("hero.stat3Value")} label={t("hero.stat3Label")}/>
            <Stat value={t("hero.stat4Value")} label={t("hero.stat4Label")}/>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section id="intro" className="py-20" style={{ background: "var(--z-bg-2)" }}>
        <div className="z-container grid md:grid-cols-[1fr_1.1fr] gap-12 items-start">
          <div>
            <div className="aspect-[5/6] rounded-[24px] overflow-hidden border border-[color:var(--z-line)]">
              <img src={INTRO_IMG} alt="New residential development" className="z-img" loading="lazy"/>
            </div>
          </div>
          <div>
            <div className="z-chip mb-4"><span className="dot"/>{t("intro.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("intro.title")}</h2>
            <div className="mt-6 space-y-4 text-[16px] leading-relaxed text-[color:var(--z-ink-2)]">
              <p>{t("intro.p1")}</p>
              <p>{t("intro.p2")}</p>
              <p>{t("intro.p3")}</p>
              <p>{t("intro.p4")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20">
        <div className="z-container">
          <div className="max-w-[720px]">
            <div className="z-chip mb-4"><span className="dot"/>{t("how.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("how.title")}</h2>
            <p className="mt-4 text-[16px] text-[color:var(--z-ink-2)]">{t("how.lead")}</p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <ClipboardCheck size={20}/>, titleKey: "how.step1Title", textKey: "how.step1Text" },
              { icon: <Coins size={20}/>, titleKey: "how.step2Title", textKey: "how.step2Text" },
              { icon: <Eye size={20}/>, titleKey: "how.step3Title", textKey: "how.step3Text" },
              { icon: <Wallet size={20}/>, titleKey: "how.step4Title", textKey: "how.step4Text" },
            ].map((s, i) => (
              <div key={i} className="z-card relative">
                <span className="absolute -top-3 -left-3 inline-flex items-center justify-center w-10 h-10 rounded-full text-[13px] font-semibold" style={{ background: "var(--z-ink)", color: "#F5F1EA" }}>
                  0{i + 1}
                </span>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4" style={{ background: "rgba(15,59,54,0.08)", color: "var(--z-brand)" }}>
                  {s.icon}
                </div>
                <h3 className="font-display text-[20px] leading-tight">{t(s.titleKey)}</h3>
                <p className="mt-2 text-[14px] text-[color:var(--z-ink-2)]">{t(s.textKey)}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 max-w-[720px] text-[15px] text-[color:var(--z-muted)] italic">
            {t("how.note")}
          </p>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="py-20" style={{ background: "#0E1B1A", color: "#EFE8D8" }}>
        <div className="z-container">
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 items-end">
            <div>
              <div className="z-chip mb-4" style={{ background: "rgba(231,201,137,0.14)", color: "#E7C989" }}>
                <span className="dot" style={{ background: "#E7C989" }}/>{t("transparency.kicker")}
              </div>
              <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">
                {t("transparency.title")}
              </h2>
            </div>
            <p className="text-[16px] opacity-80">
              {t("transparency.lead")}
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { icon: <LineChart size={20}/>, titleKey: "transparency.f1Title", textKey: "transparency.f1Text" },
              { icon: <Building size={20}/>, titleKey: "transparency.f2Title", textKey: "transparency.f2Text" },
              { icon: <ShieldCheck size={20}/>, titleKey: "transparency.f3Title", textKey: "transparency.f3Text" },
            ].map((f, i) => (
              <div key={i} className="rounded-2xl p-6 border" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4" style={{ background: "rgba(231,201,137,0.15)", color: "#E7C989" }}>
                  {f.icon}
                </div>
                <h3 className="font-display text-[20px]">{t(f.titleKey)}</h3>
                <p className="mt-2 text-[14px] opacity-80">{t(f.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20">
        <div className="z-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="z-chip mb-4"><span className="dot"/>{t("projects.kicker")}</div>
              <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("projects.title")}</h2>
            </div>
            <p className="max-w-[420px] text-[15px] text-[color:var(--z-muted)]">{t("projects.lead")}</p>
          </div>

          <div className="z-grid-projects">
            {[1, 2, 3].map((i) => (
              <article key={i} className="rounded-[22px] overflow-hidden border border-[color:var(--z-line)] bg-white flex flex-col">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={PROJECT_IMAGES[i - 1]} alt={t(`projects.p${i}Title`)} className="z-img" loading="lazy"/>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-4">
                    <span className="z-tag">{t(`projects.p${i}Status`)}</span>
                    <span className="text-[12px] uppercase tracking-wider text-[color:var(--z-muted)] flex items-center gap-1">
                      <MapPin size={12}/> {t(`projects.p${i}Location`)}
                    </span>
                  </div>
                  <h3 className="font-display text-[22px] mt-3 leading-tight">{t(`projects.p${i}Title`)}</h3>
                  <p className="mt-2 text-[14px] text-[color:var(--z-ink-2)] flex-1">{t(`projects.p${i}Text`)}</p>
                  <a href={langPath(lang) + "#contact"} className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-[color:var(--z-brand)] hover:underline" data-testid={`project-cta-${i}`}>
                    {t("projects.btn")} <ArrowUpRight size={14}/>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="py-20" style={{ background: "var(--z-bg-2)" }}>
        <div className="z-container grid md:grid-cols-[1fr_1fr] gap-12 items-center">
          <div>
            <div className="z-chip mb-4"><span className="dot"/>{t("mission.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("mission.title")}</h2>
            <div className="mt-5 space-y-4 text-[16px] text-[color:var(--z-ink-2)]">
              <p>{t("mission.p1")}</p>
              <p>{t("mission.p2")}</p>
            </div>
            <ul className="mt-6 space-y-2">
              {["bullet1", "bullet2", "bullet3"].map((k) => (
                <li key={k} className="flex items-start gap-2 text-[15px]">
                  <span className="mt-0.5 inline-flex w-5 h-5 rounded-full items-center justify-center" style={{ background: "var(--z-brand)", color: "#E7C989" }}>
                    <Check size={12}/>
                  </span>
                  {t(`mission.${k}`)}
                </li>
              ))}
            </ul>
            <a href={langPath(lang) + "#contact"} className="z-btn z-btn-primary mt-8" data-testid="mission-cta">
              {t("mission.cta")} <ArrowRight size={16}/>
            </a>
          </div>
          <div>
            <div className="aspect-[5/6] rounded-[24px] overflow-hidden border border-[color:var(--z-line)]">
              <img src={MISSION_IMG} alt="Zagreb residential district" className="z-img" loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="z-container">
          <div className="max-w-[720px]">
            <div className="z-chip mb-4"><span className="dot"/>{t("testimonials.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("testimonials.title")}</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <blockquote key={i} className="z-card">
                <p className="text-[15px] leading-relaxed">“{t(`testimonials.t${i}Text`)}”</p>
                <footer className="mt-4 flex items-center gap-3">
                  <span className="inline-flex w-9 h-9 rounded-full items-center justify-center" style={{ background: "var(--z-brand)", color: "#E7C989" }}>
                    <Users size={16}/>
                  </span>
                  <div>
                    <div className="text-[14px] font-semibold">{t(`testimonials.t${i}Name`)}</div>
                    <div className="text-[12px] text-[color:var(--z-muted)]">{t(`testimonials.t${i}Role`)}</div>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20" style={{ background: "var(--z-bg-2)" }}>
        <div className="z-container grid md:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="z-chip mb-4"><span className="dot"/>{t("faq.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("faq.title")}</h2>
          </div>
          <div className="divide-y divide-[color:var(--z-line)]">
            {[1, 2, 3, 4, 5].map((i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-display text-[18px] md:text-[20px]">{t(`faq.q${i}`)}</span>
                  <span className="ml-4 inline-flex w-8 h-8 rounded-full items-center justify-center border border-[color:var(--z-line)] group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-[15px] text-[color:var(--z-ink-2)]">{t(`faq.a${i}`)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20">
        <div className="z-container grid md:grid-cols-[1fr_1.1fr] gap-12">
          <div>
            <div className="z-chip mb-4"><span className="dot"/>{t("contact.kicker")}</div>
            <h2 className="font-display text-[34px] md:text-[46px] leading-[1.05]">{t("contact.title")}</h2>
            <p className="mt-4 text-[16px] text-[color:var(--z-ink-2)] max-w-[520px]">{t("contact.lead")}</p>
            <div className="mt-8 space-y-4 text-[14px]">
              <div className="flex items-start gap-3">
                <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center" style={{ background: "rgba(15,59,54,0.08)", color: "var(--z-brand)" }}>
                  <MapPin size={16}/>
                </span>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-[color:var(--z-muted)]">{t("contact.office")}</div>
                  <div>{t("footer.address")}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center" style={{ background: "rgba(15,59,54,0.08)", color: "var(--z-brand)" }}>
                  <Phone size={16}/>
                </span>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-[color:var(--z-muted)]">{t("contact.phone")}</div>
                  <a href="tel:+38516345720">+385 1 6345 720</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center" style={{ background: "rgba(15,59,54,0.08)", color: "var(--z-brand)" }}>
                  <Mail size={16}/>
                </span>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-[color:var(--z-muted)]">{t("contact.emailLabel")}</div>
                  <a href="mailto:hello@zynteroq.com">hello@zynteroq.com</a>
                </div>
              </div>
            </div>
          </div>

          <div className="z-card">
            <ContactForm/>
          </div>
        </div>
      </section>
    </main>
  );
}
